import { useState } from "react";
import { Link } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { ShieldCheck, CaretRight } from "@phosphor-icons/react";
import { CELL, fmtD, fmtShort, monthLabel, Pill, StatePill, durationText } from "./ppmUtils";

/** One occurrence dot — click for details / actions. */
function Cell({ row, cell, canEdit, onChanged, readOnly }) {
  const [busy, setBusy] = useState(false);
  const c = CELL[cell.state] || CELL.planned;
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged?.(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Action failed"); } finally { setBusy(false); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`h-4 w-4 rounded-full ${c.dot} ${c.ring} hover:scale-125 transition-transform`} title={`${c.label} · ${fmtD(cell.due)}`} data-testid={`ppm-cell-${row.id}-${cell.due}`} aria-label={`${row.title} ${fmtD(cell.due)} ${c.label}`} />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 text-xs space-y-2" data-testid="ppm-cell-popover">
        <div className="flex items-center justify-between gap-2"><span className="font-semibold">{fmtD(cell.due)}</span><Pill tone={c.cls}>{c.label}</Pill></div>
        <p className="text-muted-foreground">{row.title}{row.asset_name ? ` · ${row.asset_name}` : ""}</p>
        {cell.job_id && !readOnly && <Link to={`/jobs/${cell.job_id}`} className="flex items-center gap-1 text-primary font-medium" data-testid="ppm-cell-open-job">{cell.job_reference} <CaretRight size={12} /></Link>}
        {cell.job_id && readOnly && <p className="font-medium">{cell.job_reference}</p>}
        {canEdit && !cell.job_id && cell.state !== "cancelled" && (
          <div className="flex gap-2 pt-1">
            <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => act(() => API.post(`/ppm/schedules/${row.id}/generate`, { due: cell.due }), "Job created")} data-testid="ppm-cell-generate">Create job now</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => act(() => API.post(`/ppm/schedules/${row.id}/skip`, { due: cell.due }), "Occurrence skipped")} data-testid="ppm-cell-skip">Skip</Button>
          </div>
        )}
        {canEdit && cell.job_id && cell.job_status === "scheduled" && (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => act(() => API.post(`/ppm/schedules/${row.id}/skip`, { due: cell.due }), "Skipped — job cancelled")} data-testid="ppm-cell-skip-cancel">Skip &amp; cancel job</Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** 52-week planner: sticky schedule column + one column per week. */
export function PpmGrid({ data, canEdit, onChanged, onEdit, readOnly }) {
  if (!data) return null;
  const { columns, groups } = data;
  if (!groups.length) return <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="ppm-grid-empty">No planned maintenance in this view yet.</div>;
  const months = [];
  columns.forEach((col) => {
    const d = new Date(col.start + "T00:00:00");
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const last = months[months.length - 1];
    if (last && last.key === key) last.span += 1; else months.push({ key, label: monthLabel(col.start, columns.length > 26), span: 1 });
  });
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden" data-testid="ppm-grid">
      <div className="overflow-x-auto">
        <table className="text-xs border-collapse" style={{ minWidth: 320 + columns.length * 28 }}>
          <thead>
            <tr className="bg-muted/30">
              <th className="sticky left-0 z-20 bg-card border-b border-r border-border px-3 py-1.5 text-left font-semibold w-[320px] min-w-[320px]">Schedule</th>
              {months.map((m) => <th key={m.key} colSpan={m.span} className="border-b border-l border-border px-1 py-1 text-left font-medium text-muted-foreground">{m.label}</th>)}
            </tr>
            <tr className="bg-muted/10">
              <th className="sticky left-0 z-20 bg-card border-b border-r border-border px-3 py-1 text-left text-[10px] text-muted-foreground font-normal">Property · task · frequency</th>
              {columns.map((c) => <th key={c.index} className={`border-b border-border px-0 py-1 text-center text-[9px] font-normal ${c.current ? "bg-primary/15 text-primary font-semibold" : "text-muted-foreground"}`} data-testid={c.current ? "ppm-week-current" : undefined}>{new Date(c.start + "T00:00:00").getDate()}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <GroupRows key={g.property_id || g.property_name} group={g} columns={columns} canEdit={canEdit} onChanged={onChanged} onEdit={onEdit} readOnly={readOnly} />
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-3 px-3 py-2 border-t border-border text-[10px] text-muted-foreground" data-testid="ppm-legend">
        {Object.entries(CELL).filter(([k]) => k !== "cancelled").map(([k, v]) => <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded-full ${v.dot}`} />{v.label}</span>)}
      </div>
    </div>
  );
}

function GroupRows({ group, columns, canEdit, onChanged, onEdit, readOnly }) {
  return (
    <>
      <tr className="bg-muted/20">
        <td className="sticky left-0 z-10 bg-muted/20 backdrop-blur border-b border-r border-border px-3 py-1.5 font-semibold" colSpan={1} data-testid={`ppm-group-${group.property_id || "none"}`}>
          {group.property_name}{group.client_name && <span className="ml-2 font-normal text-muted-foreground">{group.client_name}</span>}
          <span className="ml-2 font-normal text-muted-foreground">· {group.rows.length} schedule{group.rows.length === 1 ? "" : "s"}</span>
        </td>
        <td colSpan={columns.length} className="border-b border-border" />
      </tr>
      {group.rows.map((row) => {
        const byWeek = {};
        (row.cells || []).forEach((c) => { (byWeek[c.week] = byWeek[c.week] || []).push(c); });
        return (
          <tr key={row.id} className="hover:bg-muted/10" data-testid={`ppm-row-${row.id}`}>
            <td className="sticky left-0 z-10 bg-card border-b border-r border-border px-3 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                {row.statutory && <ShieldCheck size={13} className="text-primary shrink-0" title="Statutory" />}
                <button className={`truncate text-left font-medium ${canEdit && onEdit ? "hover:text-primary" : ""}`} onClick={() => canEdit && onEdit?.(row)} data-testid={`ppm-row-title-${row.id}`}>{row.title}</button>
                <StatePill state={row.state} testid={`ppm-row-state-${row.id}`} />
              </div>
              <p className="text-[10px] text-muted-foreground truncate">
                {row.frequency_label}{row.duration_minutes ? ` · ${durationText(row.duration_minutes)}` : ""}{row.asset_name ? ` · ${row.asset_name}` : ""}{row.operative_names?.length ? ` · ${row.operative_names.join(", ")}` : row.vendor_name ? ` · ${row.vendor_name}` : ""}
                {row.next_due ? ` · next ${fmtShort(row.next_due)}` : ""}
              </p>
            </td>
            {columns.map((c) => (
              <td key={c.index} className={`border-b border-border p-0 text-center align-middle ${c.current ? "bg-primary/5" : ""}`}>
                <div className="flex items-center justify-center gap-0.5 h-8">
                  {(byWeek[c.index] || []).map((cell) => <Cell key={cell.due} row={row} cell={cell} canEdit={canEdit} onChanged={onChanged} readOnly={readOnly} />)}
                </div>
              </td>
            ))}
          </tr>
        );
      })}
    </>
  );
}
